<template>
  <div class="provider-invoice-upload mt-2">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-md-center flex-column flex-md-row mb-2">
      <div>
        <h2 class="font-weight-bolder mb-0">{{ $t('Provider Invoice Upload') }}</h2>
        <p class="text-muted mb-0">{{ $t("Upload provider's invoice detail file") }}</p>
      </div>
      <b-alert
        variant="secondary"
        show
        class="mb-0 mt-2 mt-md-0 d-flex align-items-center bg-white border"
      >
        <div class="alert-body d-flex align-items-center">
          <span class="text-body mr-1">{{
            $t('Only 1 record can be uploaded for each provider and period.')
          }}</span>
          <feather-icon icon="InfoIcon" size="18" class="text-muted" />
        </div>
      </b-alert>
    </div>

    <!-- Upload Form Card -->
    <b-card no-body class="p-2 mb-3 shadow-sm rounded">
      <b-row class="mb-2">
        <b-col :md="isFedex ? 4 : 6">
          <b-form-group :label="$t('Provider')">
            <v-select
              v-model="selectedProvider"
              :options="providers"
              label="name"
              :reduce="provider => provider.code || provider.id"
              :placeholder="$t('Select Provider')"
              class="w-100"
            />
          </b-form-group>
        </b-col>

        <b-col :md="isFedex ? 4 : 6">
          <b-form-group :label="$t('Invoice Period')">
            <b-input-group class="input-group-merge">
              <flat-pickr
                :key="isFedex ? 'fedex-date' : 'ptt-month'"
                v-model="selectedPeriod"
                class="form-control"
                :placeholder="periodPlaceholder"
                :config="isFedex ? singleDatePickerConfig : monthPickerConfig"
              />
              <b-input-group-append is-text>
                <feather-icon icon="CalendarIcon" />
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col v-if="isFedex" md="4">
          <b-form-group :label="$t('Invoice Exchange Rate')">
            <b-input-group class="input-group-merge">
              <b-form-input
                v-model="exchangeRate"
                type="text"
                placeholder="Örn: 34.2510"
                :class="{ 'border-danger': isFedex && !isValidExchangeRate && exchangeRate }"
                @keypress="allowNumberAndDot"
                @input="onRateInput"
              />
              <b-input-group-append is-text class="exchange-rate-suffix">
                <span class="suffix-symbol font-weight-bolder">₺</span>
                <div class="suffix-text text-left ml-50">
                  <span class="suffix-title font-weight-bolder">TRY</span>
                  <span class="suffix-sub font-small-1">/ USD</span>
                </div>
              </b-input-group-append>
            </b-input-group>
            <small v-if="!isValidExchangeRate" class="text-danger d-block mt-25">
              {{ $t('Exchange rate must be entered before uploading.') }}
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Dropzone File Area -->
      <div>
        <label class="d-block font-weight-bold">{{ $t('File') }}</label>
        <div
          class="d-flex flex-column align-items-center justify-content-center py-4 border rounded cursor-pointer"
          style="border: 2px dashed #d8d6de; min-height: 180px; position: relative"
          @dragover.prevent
          @drop.prevent="handleFile"
          :class="{ 'border-success bg-light-success': file }"
        >
          <div v-if="!file" class="text-center">
            <feather-icon icon="UploadIcon" size="32" class="text-secondary mb-1" />
            <h6 class="font-weight-bold mb-50">{{ $t('Provider Invoice File Area Text') }}</h6>
            <small class="text-muted">{{ $t('Provider Invoice File Area SubText') }}</small>
            <b-form-file
              v-model="file"
              accept=".xlsx"
              @change="handleFile"
              class="d-none"
              ref="fileInput"
              plain
            />
            <a
              href="javascript:void(0)"
              @click="$refs.fileInput.$el.click()"
              class="stretched-link"
            ></a>
          </div>
          <div v-else class="text-center w-100">
            <feather-icon icon="CheckCircleIcon" size="32" class="text-success mb-1" />
            <h6 class="font-weight-bold text-success">{{ file.name }}</h6>
            <small class="text-muted d-block">{{ formatBytes(file.size) }}</small>
            <b-button variant="flat-danger" size="sm" class="mt-1" @click="clearFile">
              <feather-icon icon="Trash2Icon" class="mr-50" />
              {{ $t('Remove File') }}
            </b-button>
          </div>
        </div>
      </div>

      <div class="mt-3">
        <b-button
          variant="primary"
          @click="initiateUpload"
          :disabled="isUploadDisabled"
          class="d-flex align-items-center"
        >
          <feather-icon icon="UploadCloudIcon" class="mr-50" />
          <span>{{ isUploading ? $t('Processing...') : $t('Upload') }}</span>
        </b-button>
      </div>
    </b-card>

    <!-- Table of Uploaded Records -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h4 class="font-weight-bolder mb-0">{{ $t('Uploaded Records') }}</h4>
      <b-button
        variant="outline-primary"
        size="sm"
        class="d-flex align-items-center"
        @click="fetchRecords"
      >
        <feather-icon icon="RotateCwIcon" size="14" class="mr-50" />
        <span>{{ $t('Refresh') }}</span>
      </b-button>
    </div>

    <b-card no-body class="shadow-sm rounded">
      <vue-good-table
        :columns="columns"
        :rows="records"
        styleClass="vgt-table striped"
        :pagination-options="paginationOptions"
      >
        <table-no-result-found slot="emptystate" />
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'status'" class="d-flex align-items-center">
            <b-badge
              :variant="statusVariant(props.row.statusCode)"
              v-b-tooltip.hover
              :title="props.row.statusDescription"
            >
              {{ props.row.status }}
            </b-badge>
            <feather-icon
              v-if="props.row.statusCode === 'failed'"
              icon="AlertCircleIcon"
              class="ml-50 text-danger cursor-pointer"
              v-b-tooltip.hover
              :title="props.row.errorMessage || $t('Operation failed')"
              @click="openErrorDetail(props.row)"
            />
          </span>
          <span v-else-if="props.column.field === 'actions'" class="d-flex align-items-center">
            <!-- Row-based Trigger Reconciliation Action for Authorized Users -->
            <template v-if="canTriggerReconciliation">
              <!-- If file upload/processing stage -> show disabled progress icon -->
              <b-button
                v-if="isRowFileProcessing(props.row)"
                variant="flat-secondary"
                size="sm"
                class="btn-icon mr-50"
                disabled
                v-b-tooltip.hover
                :title="props.row.statusDescription || $t('File Processing')"
              >
                <feather-icon icon="ClockIcon" size="14" class="text-secondary" />
              </b-button>

              <!-- If reconciliation in progress -> show disabled progress icon -->
              <b-button
                v-else-if="isRowReconciliationInProgress(props.row)"
                variant="flat-secondary"
                size="sm"
                class="btn-icon mr-50"
                disabled
                v-b-tooltip.hover
                :title="$t('Reconciliation is already in progress')"
              >
                <feather-icon icon="ClockIcon" size="14" class="text-warning" />
              </b-button>

              <!-- If reconciliation already completed -> Re-run button with Force confirmation -->
              <b-button
                v-else-if="props.row.statusCode === 'reconciliation_completed'"
                variant="outline-warning"
                size="sm"
                class="btn-icon mr-50"
                :disabled="props.row.isTriggering"
                v-b-tooltip.hover
                :title="$t('Re-run')"
                @click="onRowTriggerClick(props.row)"
              >
                <b-spinner v-if="props.row.isTriggering" small />
                <feather-icon v-else icon="RotateCwIcon" size="14" />
              </b-button>

              <!-- For all other statuses (file_processed, failed, etc.) -> Trigger button -->
              <b-button
                v-else
                variant="outline-success"
                size="sm"
                class="btn-icon mr-50"
                :disabled="props.row.isTriggering"
                v-b-tooltip.hover
                :title="$t('Trigger')"
                @click="onRowTriggerClick(props.row)"
              >
                <b-spinner v-if="props.row.isTriggering" small />
                <feather-icon v-else icon="PlayIcon" size="14" />
              </b-button>
            </template>

            <!-- Delete Button -->
            <b-button
              variant="outline-danger"
              size="sm"
              class="btn-icon"
              v-b-tooltip.hover
              :title="$t('Delete')"
              @click="confirmDelete(props.row)"
            >
              <feather-icon icon="Trash2Icon" size="14" />
            </b-button>
          </span>
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
        <template slot="empty">
          <div class="text-center p-2 text-muted">
            {{ $t('No records found') }}
          </div>
        </template>
      </vue-good-table>
    </b-card>

    <!-- Modal: Overwrite Confirmation -->
    <b-modal
      v-model="showOverwriteModal"
      :title="$t('Record exists')"
      title-class="font-weight-bolder"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      :ok-title="$t('Yes')"
      :cancel-title="$t('No')"
      @ok="() => processUpload(true)"
      centered
    >
      <p>{{ $t('Overwrite confirmation message') }}</p>
    </b-modal>

    <!-- Modal: Delete Confirmation -->
    <b-modal
      v-model="showDeleteModal"
      :title="$t('Delete Confirmation Title')"
      title-class="font-weight-bolder"
      ok-variant="danger"
      cancel-variant="outline-secondary"
      :ok-title="$t('Delete')"
      :cancel-title="$t('Cancel')"
      @ok="deleteRecord"
      centered
    >
      <p>{{ getDeleteConfirmationMessage() }}</p>
    </b-modal>

    <!-- Modal: Re-run Reconciliation Confirmation (Force) -->
    <b-modal
      v-model="showReRunModal"
      :title="$t('Re-run Confirmation Title')"
      title-class="font-weight-bolder"
      ok-variant="warning"
      cancel-variant="outline-secondary"
      :ok-title="$t('Yes')"
      :cancel-title="$t('Cancel')"
      @ok="() => executeTriggerForRecord(rowToReRun, true)"
      @hidden="rowToReRun = null"
      centered
    >
      <p>{{ getReRunConfirmationMessage() }}</p>
    </b-modal>

    <!-- Modal: Error Details -->
    <b-modal
      v-model="showErrorModal"
      :title="$t('Error')"
      title-class="font-weight-bolder text-danger"
      ok-only
      ok-variant="secondary"
      :ok-title="$t('Close')"
      centered
    >
      <p class="mb-0">{{ selectedErrorMessage || $t('Operation failed') }}</p>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BFormGroup,
  BFormInput,
  BFormFile,
  BAlert,
  BBadge,
  BModal,
  BSpinner,
  BInputGroup,
  BInputGroupAppend,
  VBTooltip
} from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import flatPickr from 'vue-flatpickr-component';
import MonthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index.js';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import { Turkish } from 'flatpickr/dist/l10n/tr.js';
import vSelect from 'vue-select';
import useService from '@/auth/service/useService';
import { helper } from '@core/mixins/helper.js';
import { vueGoodTablePagination } from '@core/mixins/vueGoodTablePagination';
import { ROLES } from '@/constants/roles';
import { checkHasRole } from '@core/utils/roleAccess';

export const RECONCILIATION_POLLING_STATUSES = [
  'file_uploaded',
  'file_processing',
  'reconciliation_requested',
  'reconciliation_processing'
];

export default {
  name: 'ProviderInvoiceUpload',
  mixins: [helper, vueGoodTablePagination],
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BFormGroup,
    BFormInput,
    BFormFile,
    BAlert,
    BBadge,
    BModal,
    BSpinner,
    VueGoodTable,
    vSelect,
    flatPickr,
    BInputGroup,
    BInputGroupAppend
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  data() {
    return {
      selectedProvider: null,
      selectedPeriod: null,
      exchangeRate: '',
      file: null,
      isUploading: false,
      showOverwriteModal: false,
      showDeleteModal: false,
      recordToDelete: null,
      providers: [],
      records: [],
      pollingTimeout: null,

      // Trigger Reconciliation State
      showReRunModal: false,
      rowToReRun: null,

      // Error Details State
      showErrorModal: false,
      selectedErrorMessage: ''
    };
  },
  beforeDestroy() {
    if (this.pollingTimeout) clearTimeout(this.pollingTimeout);
  },
  watch: {
    selectedProvider() {
      this.selectedPeriod = null;
      this.exchangeRate = '';
    }
  },
  computed: {
    canTriggerReconciliation() {
      return checkHasRole([ROLES.ADMIN, ROLES.ACCOUNT_ADMIN]);
    },
    currentProvider() {
      return (
        this.providers.find(
          p =>
            String(p.id) === String(this.selectedProvider) ||
            String(p.code) === String(this.selectedProvider)
        ) || null
      );
    },
    isFedex() {
      if (!this.currentProvider) return false;
      const name = (this.currentProvider.name || '').toLowerCase();
      const code = (this.currentProvider.code || '').toLowerCase();
      return name.includes('fedex') || code.includes('fedex');
    },
    isValidExchangeRate() {
      if (!this.exchangeRate) return false;
      const num = parseFloat(String(this.exchangeRate).replace(',', '.'));
      return !Number.isNaN(num) && num > 0;
    },
    periodPlaceholder() {
      return this.isFedex ? this.$t('Select Date') : this.$t('Select Period');
    },
    isUploadDisabled() {
      if (!this.selectedProvider || !this.selectedPeriod || this.isUploading) {
        return true;
      }
      return this.isFedex && !this.isValidExchangeRate;
    },
    monthPickerConfig() {
      return {
        altInput: true,
        altFormat: 'F Y',
        plugins: [
          new MonthSelectPlugin({
            shorthand: true,
            dateFormat: 'Y-m',
            altFormat: 'F Y',
            theme: 'light'
          })
        ],
        locale: this.$i18n.locale === 'tr' ? Turkish : undefined,
        maxDate: new Date()
      };
    },
    singleDatePickerConfig() {
      return {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'd-m-Y',
        locale: this.$i18n.locale === 'tr' ? Turkish : undefined,
        maxDate: new Date()
      };
    },
    columns() {
      return [
        { label: this.$t('Provider'), field: 'providerName' },
        { label: this.$t('Invoice Period'), field: 'periodFormatted' },
        { label: this.$t('File Name'), field: 'fileName' },
        { label: this.$t('Total Rows'), field: 'totalRows' },
        { label: this.$t('Upload Date'), field: 'uploadDate' },
        { label: this.$t('Uploaded User'), field: 'uploadedBy' },
        { label: this.$t('Status'), field: 'status' },
        { label: this.$t('Action'), field: 'actions', sortable: false }
      ];
    }
  },
  async created() {
    await this.fetchProviders();
    this.fetchRecords();
  },
  methods: {
    allowNumberAndDot(e) {
      if (!/[0-9.,]/.test(e.key)) {
        e.preventDefault();
      }
    },
    onRateInput(val) {
      const clean = (val || '').replace(/,/g, '.').replace(/[^0-9.]/g, '');
      this.$nextTick(() => {
        this.exchangeRate = clean;
      });
    },
    statusVariant(statusCode) {
      const variants = {
        file_uploaded: 'light-warning',
        file_processing: 'warning',
        file_processed: 'light-info',
        reconciliation_requested: 'light-primary',
        reconciliation_processing: 'warning',
        reconciliation_completed: 'success',
        failed: 'danger'
      };
      return variants[statusCode] || 'secondary';
    },
    statusLabel(statusCode) {
      const labels = {
        file_uploaded: this.$t('File Uploaded'),
        file_processing: this.$t('File Processing'),
        file_processed: this.$t('File Processed'),
        reconciliation_requested: this.$t('Reconciliation Requested'),
        reconciliation_processing: this.$t('Reconciliation Processing'),
        reconciliation_completed: this.$t('Reconciliation Completed'),
        failed: this.$t('Error')
      };
      return labels[statusCode] || this.$t('Unknown Status');
    },
    statusDescription(statusCode) {
      const descs = {
        file_uploaded: this.$t('File received, waiting to be processed'),
        file_processing: this.$t('File is being read and records are being written'),
        file_processed: this.$t('File processed successfully'),
        reconciliation_requested: this.$t(
          'Reconciliation request received, waiting for calculation'
        ),
        reconciliation_processing: this.$t('Reconciliation is being calculated'),
        reconciliation_completed: this.$t('Reconciliation completed successfully'),
        failed: this.$t('Operation failed')
      };
      return descs[statusCode] || '';
    },
    async fetchProviders() {
      try {
        if (useService?.reportService?.getReconciliationProviders) {
          const res = await useService.reportService.getReconciliationProviders();
          let raw = [];
          if (Array.isArray(res?.data)) {
            raw = res.data;
          } else if (Array.isArray(res)) {
            raw = res;
          }
          if (raw.length > 0) {
            this.providers = raw.map(item => {
              if (typeof item === 'string') {
                return { id: item, code: item, name: item };
              }
              const code = item.code || item.provider_code || item.id;
              const name = item.name || item.provider_name || code;
              return {
                ...item,
                id: item.id || code,
                code,
                name
              };
            });
            return;
          }
        }
      } catch (e) {
        // Fallback to provider service
      }

      const res = await useService?.providerService?.getAllProviders({ limit: 100 });
      this.providers = (res?.data || []).map(p => ({
        ...p,
        code: p.code || p.name || p.id
      }));
    },
    mapRecord(item) {
      const pCode = item.provider_code || item.provider_id;
      const provider = this.providers.find(
        p => String(p.code) === String(pCode) || String(p.id) === String(pCode)
      );
      const providerCode = item.provider_code || provider?.code || item.provider_id;
      const statusCode = (item.status || '').toLowerCase();

      return {
        id: item.id,
        uploadId: item.upload_id || item.id,
        providerId: item.provider_id,
        providerCode,
        providerName: provider?.name || pCode || '-',
        rawTerm: item.term,
        termType: item.term_type,
        periodFormatted: this.formatPeriod(item.term, item.term_type),
        fileName: item.file_name,
        totalRows: item.total_rows != null ? item.total_rows.toLocaleString('tr-TR') : '-',
        uploadDate: item.created_at ? new Date(item.created_at).toLocaleString('tr-TR') : '-',
        uploadedBy: item.uploaded_by_email || '-',
        status: this.statusLabel(statusCode),
        statusDescription: this.statusDescription(statusCode),
        statusCode,
        errorMessage: item.error_message,
        isTriggering: false
      };
    },
    async fetchRecords() {
      try {
        const res = await useService?.reportService?.getProviderBillings({ skip: 0, limit: 100 });
        let items = [];
        if (Array.isArray(res?.data)) {
          items = res.data;
        } else if (Array.isArray(res)) {
          items = res;
        }
        this.records = items.map(item => this.mapRecord(item));
        this.checkPolling();
      } catch (err) {
        // Handle fetch error quietly
      }
    },
    checkPolling() {
      if (this.pollingTimeout) clearTimeout(this.pollingTimeout);
      const hasActiveProcessing = this.records.some(r =>
        RECONCILIATION_POLLING_STATUSES.includes(r.statusCode)
      );
      if (hasActiveProcessing) {
        this.pollingTimeout = setTimeout(() => this.fetchRecords(), 6000);
      }
    },
    handleFile(e) {
      const files = e.target.files || e.dataTransfer?.files;
      if (files && files.length && this.validateFile(files[0], ['xlsx'], 200)) {
        this.file = files[0];
      }
    },
    clearFile() {
      this.file = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.reset();
      }
    },
    initiateUpload() {
      if (this.isUploadDisabled) return;
      if (!this.file) {
        this.$refs.fileInput.$el.click();
        return;
      }
      this.processUpload(false);
    },
    async processUpload(isOverwrite = false) {
      this.showOverwriteModal = false;
      this.isUploading = true;

      const providerName = this.currentProvider?.name || this.currentProvider?.code;
      const formData = new FormData();
      formData.append('file', this.file);
      formData.append('overwrite', isOverwrite ? 'true' : 'false');

      if (this.isFedex) {
        formData.append('term', this.selectedPeriod);
        formData.append('term_type', 'Week');
        formData.append('usd_to_try_rate', String(this.exchangeRate).trim().replace(',', '.'));
      } else {
        formData.append('term', `${this.selectedPeriod}-01`);
        formData.append('term_type', 'month');
      }

      try {
        await useService.reportService.uploadProviderBilling(providerName, formData);
        this.showSuccessMessage(this.$t('Operation Complete Successfully'), this.$t('Success'));
        await this.fetchRecords();
        this.clearForm();
      } catch (err) {
        const data = err.response?.data || err;
        const status = err.response?.status || data?.status;
        const isConflict = status === 409 || data?.code === 'PROVIDER_BILLING_ALREADY_EXISTS';

        if (!isOverwrite && isConflict) {
          this.showOverwriteModal = true;
          this.isUploading = false;
          return;
        }

        const msg = data?.detail || data?.message || this.$t('Error upload Message');
        this.showErrorMessage(msg, this.$t('Error'));
        this.clearForm();
      }
    },
    isRowFileProcessing(row) {
      return row.statusCode === 'file_uploaded' || row.statusCode === 'file_processing';
    },
    isRowReconciliationInProgress(row) {
      return (
        row.statusCode === 'reconciliation_requested' ||
        row.statusCode === 'reconciliation_processing'
      );
    },
    openErrorDetail(row) {
      this.selectedErrorMessage = row.errorMessage || this.$t('Operation failed');
      this.showErrorModal = true;
    },
    onRowTriggerClick(row) {
      if (!row || this.isRowReconciliationInProgress(row) || this.isRowFileProcessing(row)) return;

      if (row.statusCode === 'reconciliation_completed') {
        this.rowToReRun = row;
        this.showReRunModal = true;
        return;
      }

      this.executeTriggerForRecord(row, false);
    },
    async executeTriggerForRecord(row, isForce = false) {
      if (!row) return;

      this.$set(row, 'isTriggering', true);
      this.showReRunModal = false;

      const payload = {
        upload_id: row.uploadId || row.id,
        force: Boolean(isForce)
      };

      try {
        await useService.reportService.triggerReconciliation(payload);
        this.showSuccessMessage(this.$t('Reconciliation started'), this.$t('Info'));
        this.$set(row, 'statusCode', 'reconciliation_requested');
        this.$set(row, 'status', this.statusLabel('reconciliation_requested'));
        this.$set(row, 'statusDescription', this.statusDescription('reconciliation_requested'));
        this.checkPolling();
        await this.fetchRecords();
      } catch (err) {
        const errorData = err.response?.data || err;
        const msg =
          errorData?.detail ||
          errorData?.message ||
          (typeof errorData === 'string' ? errorData : this.$t('Failed to trigger reconciliation'));

        this.showErrorMessage(msg, this.$t('Error'));
      } finally {
        this.$set(row, 'isTriggering', false);
        this.rowToReRun = null;
      }
    },
    getReRunConfirmationMessage() {
      if (!this.rowToReRun) return this.$t('Re-run Confirmation Message');
      const provider = this.rowToReRun.providerName || this.rowToReRun.providerCode || '';
      const period = this.rowToReRun.periodFormatted || '';
      return `${provider} (${period}) ${this.$t('Re-run Confirmation Message')}`;
    },
    confirmDelete(row) {
      this.recordToDelete = row;
      this.showDeleteModal = true;
    },
    getDeleteConfirmationMessage() {
      if (!this.recordToDelete) return '';
      return this.$t('Provider Invoice Delete Confirmation Message')
        .replace('{provider}', this.recordToDelete.providerName)
        .replace('{period}', this.recordToDelete.periodFormatted);
    },
    async deleteRecord() {
      if (!this.recordToDelete) {
        this.showDeleteModal = false;
        return;
      }

      try {
        await useService.reportService.deleteProviderBilling(this.recordToDelete.id);
        this.records = this.records.filter(r => r.id !== this.recordToDelete.id);
        this.showSuccessMessage(this.$t('Operation Complete Successfully'), this.$t('Success'));
      } catch (err) {
        const data = err.response?.data || err;
        const msg = data?.detail || data?.message || this.$t('Error');
        this.showErrorMessage(msg, this.$t('Error'));
      } finally {
        this.showDeleteModal = false;
        this.recordToDelete = null;
      }
    },
    clearForm() {
      this.isUploading = false;
      this.clearFile();
      this.selectedProvider = null;
      this.selectedPeriod = null;
      this.exchangeRate = '';
    }
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';

.dark-layout {
  .flatpickr-monthSelect-month {
    color: #d0d2d6;
  }

  .flatpickr-monthSelect-month.flatpickr-disabled {
    color: #6e6b7b !important;
    opacity: 0.4 !important;
  }

  .flatpickr-monthSelect-month:not(.flatpickr-disabled):not(.selected):hover {
    background-color: #283046 !important;
    color: #7367f0 !important;
  }
}

.flatpickr-monthSelect-month.selected {
  background-color: #7367f0 !important;
  color: white !important;
}

.btn:disabled,
.btn.disabled {
  cursor: not-allowed !important;
}

.exchange-rate-suffix {
  .input-group-text {
    background-color: #f8f9fa !important;
    border-left: 1px solid #ebe9f1 !important;
    padding: 0.35rem 0.75rem !important;
    user-select: none;
    display: flex;
    align-items: center;
  }

  .suffix-symbol {
    font-size: 1.15rem;
    color: #0ca5cf;
    line-height: 1;
  }

  .suffix-title {
    font-size: 0.78rem;
    color: #5e5873;
    line-height: 1.1;
    letter-spacing: 0.4px;
  }

  .suffix-sub {
    font-size: 0.65rem;
    color: #b9b9c3;
    line-height: 1;
    margin-top: 1px;
    font-weight: 500;
  }
}

.dark-layout {
  .exchange-rate-suffix {
    .input-group-text {
      background-color: #40465552 !important;
      border-left-color: #3b4253 !important;
    }

    .suffix-title {
      color: #d0d2d6;
    }

    .suffix-sub {
      color: #6e6b7b;
    }
  }
}
</style>
